import {
  INavigationImageLink,
  IL1NavigationLink,
  IL3NavigationLink,
  IL3NavigationLinkGroup,
} from "@faire/deprecated-retailer-visitor-shared/components/Header/desktop/NavigationLinksBar/__internal__/ILNavigationLink";
import { isNotUndefined } from "@faire/typescript";
import { INavigationLink } from "@faire/web-api--source/indigofair/data/INavigationLink";

export const mapNavigationLinkToL1Link = (
  navigationLink: INavigationLink & {
    groupUniqueIdentifier?: string;
  }
): IL1NavigationLink => {
  const {
    name,
    deep_link_path,
    path_identifier,
    sublinks,
    groupUniqueIdentifier,
    style_group,
    ...remainingL1Fields
  } = navigationLink;

  const visibleSublinks = sublinks.filter(
    (sublink) => !isInvisibleForAssignmentLink(sublink)
  );

  /**
   * if a link has sublinks we'd consider it as a l2 link
   * it will be placed at the bottom half of the left column
   *
   * Else, if a link has preserve_order customization, we will consider it as a l3LinkBottom
   * but it will be placed below l2 links
   *
   * other links (l3 links) will be placed at the top section of the left column
   *
   * So in the left column, from top to bottom, we have l3Links, l2links and l3LinksBottom
   */
  const isL2Link = (sublink: INavigationLink) => sublink.sublinks.length > 0;
  const isL3LinkBottom = (sublink: INavigationLink) =>
    sublink.customizations.includes(
      INavigationLink.Customization.PRESERVE_ORDER
    );
  const l2Links = visibleSublinks.filter((sublink) => isL2Link(sublink));
  const l3LinksBottom = visibleSublinks.filter((sublink) =>
    isL3LinkBottom(sublink)
  );
  const l3Links = visibleSublinks.filter(
    (sublink) => !isL2Link(sublink) && !isL3LinkBottom(sublink)
  );
  return {
    name: name ?? "",
    to: deep_link_path,
    hasSublinks: sublinks.length > 0,
    categoryPathIdentifier: path_identifier,
    imageLink: extractRightImageInformation(remainingL1Fields),
    groupUniqueIdentifier,
    styleGroup: style_group,
    l2Links: l2Links.map(
      (
        {
          name,
          deep_link_path,
          path_identifier,
          customizations,
          ...remainingL2Fields
        },
        index
      ) => ({
        name: name ?? "",
        to: deep_link_path,
        hasSublinks: remainingL2Fields.sublinks.length > 0,
        categoryPathIdentifier: path_identifier,
        l3LinkGroups: mapNavigationLinkToL3Groups(remainingL2Fields),
        imageLink: extractRightImageInformation(remainingL2Fields),
        isInitiallySelected: index === 0,
        shouldAugmentColumnWidths: customizations.includes(
          INavigationLink.Customization.AUGMENT_COLUMN_WIDTHS
        ),
      })
    ),
    l3Links: l3Links.map((l3Link) => mapNavigationLinkToL3Link(l3Link)),
    l3LinksBottom: l3LinksBottom.map((l3LinkBottom) =>
      mapNavigationLinkToL3Link(l3LinkBottom)
    ),
    customImageSectionProps: {
      customizations: remainingL1Fields.customizations,
      right_image: remainingL1Fields.right_image,
      right_image_redirect_url: remainingL1Fields.right_image_redirect_url,
      right_image_alt_text: remainingL1Fields.right_image_alt_text,
      image_captions: remainingL1Fields.image_captions,
    },
    invisibleForAssignmentChildCategoryPathIdentifiers:
      getInvisibleForAssignmentCategoryPathIdentifiers(sublinks),
  };
};

const isInvisibleForAssignmentLink = (navigationLink: INavigationLink) =>
  navigationLink.style_group ===
  INavigationLink.StyleGroup.INVISIBLE_FOR_ASSIGNMENT;

const extractRightImageInformation = (
  navigationLink: Pick<
    INavigationLink,
    "right_image" | "right_image_redirect_url" | "image_captions"
  >
): INavigationImageLink | undefined => {
  const { right_image, right_image_redirect_url, image_captions } =
    navigationLink;
  if (right_image === undefined) {
    return;
  }

  return {
    to: right_image_redirect_url,
    imageUrl: right_image.url,
    // Limit captions to two lines
    captions: image_captions.filter(isNotUndefined).slice(0, 2),
  };
};

const mapNavigationLinkToL3Groups = (
  navigationLink: Pick<INavigationLink, "sublinks">
): IL3NavigationLinkGroup[] => {
  const groupHeaders = navigationLink.sublinks.filter(
    ({ is_group_header }) => is_group_header
  );

  if (groupHeaders.length === 0) {
    return [
      {
        name: "",
        l3Links: navigationLink.sublinks
          .filter((sublink) => !isInvisibleForAssignmentLink(sublink))
          .map((sublink) => mapNavigationLinkToL3Link(sublink)),
        invisibleForAssignmentL3CategoryPathIdentifiers:
          getInvisibleForAssignmentCategoryPathIdentifiers(
            navigationLink.sublinks
          ),
      },
    ];
  }
  return groupHeaders.map(mapGroupHeaderToL3Group);
};

const mapGroupHeaderToL3Group = (
  groupHeaderNavigationLink: Pick<INavigationLink, "name" | "sublinks">
): IL3NavigationLinkGroup => ({
  name: groupHeaderNavigationLink.name,
  l3Links: groupHeaderNavigationLink.sublinks
    .filter((sublink) => !isInvisibleForAssignmentLink(sublink))
    .map((sublink) => mapNavigationLinkToL3Link(sublink)),
  invisibleForAssignmentL3CategoryPathIdentifiers:
    getInvisibleForAssignmentCategoryPathIdentifiers(
      groupHeaderNavigationLink.sublinks
    ),
});

const mapNavigationLinkToL3Link = (
  navigationLink: INavigationLink
): IL3NavigationLink => {
  const { name, deep_link_path, path_identifier, sublinks } = navigationLink;
  return {
    name: name ?? "",
    to: deep_link_path,
    hasSublinks: sublinks.length > 0,
    categoryPathIdentifier: path_identifier,
  };
};

const getInvisibleForAssignmentCategoryPathIdentifiers = (
  sublinks: INavigationLink[]
) =>
  sublinks
    .filter(isInvisibleForAssignmentLink)
    .map((sublink) => sublink.path_identifier)
    .filter(isNotUndefined);
